import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse, ListExportParams } from '../types';
import { mergeAkoniAccountSearchFilters } from '@/components/page-components/akoni/akoni-account-list-filters';
import {
  AkoniAccount,
  AkoniApplication,
  AkoniBank,
  AkoniDeposit,
  AkoniDepositAccrual,
  AkoniDepositInstruction,
  AkoniDepositTransactionApiRow,
  AkoniDirectBillingReportRun,
  AkoniIndividual,
  AkoniIntroducer,
  AkoniInstructionFileDirection,
  AkoniInstructionFileRun,
  AkoniPatchAccountPayload,
  SavingsPatchBankPayload,
  AkoniPatchDepositInstructionPayload,
  AkoniPatchDepositPayload,
  AkoniProduct,
  AkoniPublicHoliday,
  AkoniTransaction,
  AkoniUpdateProductRateFields,
  SavingsProductAccrualResponse,
  SavingsProductAccrualStatus
} from '@/interfaces/Akoni.interface';

const DEFAULT_VERSION = '2026-01-20';

function filenameFromContentDisposition(
  header: string | undefined,
  fallback: string
): string {
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8?.[1]) return decodeURIComponent(utf8[1]);
  const quoted = header.match(/filename="([^"]+)"/i);
  if (quoted?.[1]) return quoted[1];
  const plain = header.match(/filename=([^;]+)/i);
  return plain?.[1]?.trim() || fallback;
}

const SAVINGS_PRODUCT_ACCRUAL_STATUSES: readonly SavingsProductAccrualStatus[] =
  ['complete', 'no_data', 'incomplete'];

/**
 * Only the API may declare the report's completeness — a response that does
 * not match the documented envelope is a contract failure, never business
 * state. Repairing it (e.g. defaulting a missing meta to no_data) would tell
 * the operator that no dated transactions exist when the truth is unknown.
 */
const isSavingsProductAccrualEnvelope = (
  payload: unknown
): payload is SavingsProductAccrualResponse => {
  if (typeof payload !== 'object' || payload === null) {
    return false;
  }
  const candidate = payload as {
    data?: unknown;
    meta?: { status?: unknown } | null;
  };
  return (
    Array.isArray(candidate.data) &&
    typeof candidate.meta === 'object' &&
    candidate.meta !== null &&
    typeof candidate.meta.status === 'string' &&
    (SAVINGS_PRODUCT_ACCRUAL_STATUSES as readonly string[]).includes(
      candidate.meta.status
    )
  );
};

const normaliseListResponse = <T>(
  response: any,
  fallbackPage?: number,
  fallbackPerPage?: number
): ListDataResponse<T> => {
  const payload = response?.data ?? response;
  const items = Array.isArray(payload)
    ? payload
    : payload?.data ?? payload?.items ?? [];
  const meta =
    payload?.meta?.pagination ??
    payload?.pagination ??
    payload?.meta ??
    payload?.page ??
    {};

  return {
    items,
    pagination: {
      page:
        meta.current_page ?? meta.page ?? meta.pageNumber ?? fallbackPage ?? 1,
      perPage:
        meta.per_page ??
        meta.perPage ??
        meta.page_size ??
        meta.pageSize ??
        fallbackPerPage ??
        items.length,
      total:
        meta.total ??
        meta.total_count ??
        meta.totalCount ??
        meta.count ??
        items.length,
      lastPage: meta.last_page ?? meta.lastPage
    }
  };
};

export class ApiAkoni extends BaseApiClient {
  private withVersionHeader() {
    return {
      headers: {
        Version: DEFAULT_VERSION
      }
    };
  }

  async listIndividuals(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniIndividual>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
        listData.filters.email = searchTerm;
      }

      this.client
        .getList('/individuals', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniIndividual>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async getIndividual(id: string | number): Promise<AkoniIndividual> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/individuals/${id}`, this.withVersionHeader())
        .then((res) => {
          resolve(res.data?.data ?? res.data);
        })
        .catch((err) => reject(err));
    });
  }

  async listAccounts(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniAccount>> {
    return new Promise((resolve, reject) => {
      listData.filters = mergeAkoniAccountSearchFilters(
        listData.filters,
        searchTerm
      );

      this.client
        .getList('/accounts', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniAccount>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async getAccount(id: string | number): Promise<AkoniAccount> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/accounts/${id}`, this.withVersionHeader())
        .then((res) => {
          resolve(res.data?.data ?? res.data);
        })
        .catch((err) => reject(err));
    });
  }

  async patchAccount(
    id: string | number,
    payload: AkoniPatchAccountPayload
  ): Promise<AkoniAccount> {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/accounts/${id}`, payload, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async listDeposits(
    listData: ListDataParams,
    searchTerm?: string,
    accountId?: string | number
  ): Promise<ListDataResponse<AkoniDeposit>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.amount = searchTerm;
      }

      if (accountId != null && accountId !== '') {
        listData.filters = { ...listData.filters, account_id: accountId };
      }

      this.client
        .getList('/deposits', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniDeposit>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async listIntroducers(
    searchTerm: string | null = null,
    page = 1,
    perPage = 20,
    filters?: { company_id?: string | number }
  ): Promise<ListDataResponse<AkoniIntroducer>> {
    return new Promise((resolve, reject) => {
      const listData: ListDataParams = {
        page,
        perPage,
        filters: {
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(filters ?? {})
        }
      };

      this.client
        .getList('/introducers', listData, this.withVersionHeader())
        .then((res) =>
          resolve(normaliseListResponse<AkoniIntroducer>(res, page, perPage))
        )
        .catch((err) => reject(err));
    });
  }

  async getDeposit(id: string | number): Promise<AkoniDeposit> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/deposits/${id}`, this.withVersionHeader())
        .then((res) => {
          resolve(res.data?.data ?? res.data);
        })
        .catch((err) => reject(err));
    });
  }

  async getDepositSummaryPdf(id: string | number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/deposits/${id}/summary-pdf`, {
          responseType: 'blob',
          headers: {
            ...this.withVersionHeader().headers,
            Accept: 'application/pdf'
          }
        })
        .then((res) => {
          resolve(new Blob([res.data], { type: 'application/pdf' }));
        })
        .catch((err) => reject(err));
    });
  }

  async patchDeposit(
    id: string | number,
    payload: AkoniPatchDepositPayload
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/deposits/${id}`, payload, this.withVersionHeader())
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async getPublicHolidays(filters: {
    start_date: string;
    end_date: string;
    division?: string;
  }): Promise<AkoniPublicHoliday[]> {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams();
      urlParams.set('filter[start_date]', filters.start_date);
      urlParams.set('filter[end_date]', filters.end_date);
      if (filters.division) {
        urlParams.set('filter[division]', filters.division);
      }

      this.client
        .get(`/public-holidays?${urlParams.toString()}`)
        .then((res) => {
          const payload = res.data?.data ?? res.data;
          resolve(Array.isArray(payload) ? payload : []);
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * The pooled product daily accrual report. The API owns every monetary value
   * and the completeness metadata — including the no-data state — so the
   * response is passed through unchanged, and a response that does not match
   * the documented envelope is rejected so the modal shows its failure state.
   */
  async listProductAccruals(
    productId: string | number,
    params: { end_date: string; page?: number; perPage?: number }
  ): Promise<SavingsProductAccrualResponse> {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams();
      urlParams.set('filter[end_date]', params.end_date);
      if (params.page !== undefined) {
        urlParams.set('page[number]', String(params.page));
      }
      if (params.perPage !== undefined) {
        urlParams.set('page[size]', String(params.perPage));
      }

      this.client
        .get(
          `/products/${productId}/accruals?${urlParams.toString()}`,
          this.withVersionHeader()
        )
        .then((res) => {
          const payload = res.data;
          if (!isSavingsProductAccrualEnvelope(payload)) {
            reject(new Error('Malformed product accruals response'));
            return;
          }
          resolve(payload);
        })
        .catch((err) => reject(err));
    });
  }

  async listDepositAccruals(
    depositId: string | number,
    filters: { start_date: string; end_date: string }
  ): Promise<AkoniDepositAccrual[]> {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams();
      urlParams.set('filter[start_date]', filters.start_date);
      urlParams.set('filter[end_date]', filters.end_date);

      this.client
        .get(
          `/deposits/${depositId}/accruals?${urlParams.toString()}`,
          this.withVersionHeader()
        )
        .then((res) => {
          const payload = res.data?.data ?? res.data;
          resolve(Array.isArray(payload) ? payload : []);
        })
        .catch((err) => reject(err));
    });
  }

  async listDepositTransactions(
    depositId: string | number,
    listData: ListDataParams
  ): Promise<ListDataResponse<AkoniDepositTransactionApiRow>> {
    return new Promise((resolve, reject) => {
      const urlParams = new URLSearchParams();
      if (listData.page) urlParams.set('page', String(listData.page));
      if (listData.perPage) urlParams.set('per_page', String(listData.perPage));
      if (listData.filters) {
        Object.entries(listData.filters).forEach(([filterKey, filterValue]) => {
          if (
            filterValue !== undefined &&
            filterValue !== null &&
            filterValue !== ''
          ) {
            urlParams.set(`filter[${filterKey}]`, String(filterValue));
          }
        });
      }

      const query = urlParams.toString();
      const url = `/deposits/${depositId}/transactions${
        query ? `?${query}` : ''
      }`;

      this.client
        .get(url, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniDepositTransactionApiRow>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async getInstruction(id: string | number): Promise<Record<string, unknown>> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/instructions/${id}`, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async getApplication(id: string | number): Promise<AkoniApplication> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/applications/${id}`, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async listApplications(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniApplication>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/applications', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniApplication>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async updateDynamoDbItem(
    id: string | number,
    key: string,
    value: string
  ): Promise<unknown> {
    return new Promise((resolve, reject) => {
      const payload = new URLSearchParams();
      payload.set('key', key);
      payload.set('value', value);
      this.client
        .put(`/dynamo-db-items/${id}`, payload, {
          ...this.withVersionHeader(),
          headers: {
            ...this.withVersionHeader().headers,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        })
        .then((res) => resolve(res.data))
        .catch((err) => reject(err));
    });
  }

  async listDirectBillingReports(
    listData: ListDataParams
  ): Promise<ListDataResponse<AkoniDirectBillingReportRun>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList('/direct-billing-reports', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniDirectBillingReportRun>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async listTransactions(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniTransaction>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.transaction_reason = searchTerm;
      }
      this.client
        .getList('/transactions', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniTransaction>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async getTransaction(id: string | number): Promise<AkoniTransaction> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/transactions/${id}`, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async listDepositInstructions(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniDepositInstruction>> {
    return new Promise((resolve, reject) => {
      const trimmedSearch = searchTerm?.trim();
      if (trimmedSearch) {
        if (!listData.filters) listData.filters = {};
        listData.filters.search = trimmedSearch;
      }
      this.client
        .getList('/deposit-instructions', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniDepositInstruction>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async exportDepositInstructions(
    listData: ListExportParams,
    searchTerm?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const trimmedSearch = searchTerm?.trim();
    const exportData: ListExportParams = {
      filters: listData.filters,
      sort: listData.sort,
      group: listData.group
    };
    if (trimmedSearch) {
      exportData.filters = {
        ...exportData.filters,
        search: trimmedSearch
      };
    }

    const response = await this.client.getList(
      '/deposit-instructions/export',
      exportData,
      {
        responseType: 'blob',
        headers: {
          ...this.withVersionHeader().headers,
          Accept: 'text/csv'
        }
      }
    );

    const direction =
      typeof exportData.filters?.direction === 'string'
        ? exportData.filters.direction
        : 'deposit';
    const filename = filenameFromContentDisposition(
      response.headers['content-disposition'],
      `savings-${direction}-instructions.csv`
    );

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename
    };
  }

  async getDepositInstruction(
    id: string | number
  ): Promise<AkoniDepositInstruction> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/deposit-instructions/${id}`, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async patchDepositInstruction(
    id: string | number,
    payload: AkoniPatchDepositInstructionPayload
  ): Promise<AkoniDepositInstruction> {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/deposit-instructions/${id}`, payload, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async cancelDepositInstruction(
    id: string | number,
    payload: { reason?: string | null } = {}
  ): Promise<AkoniDepositInstruction> {
    return new Promise((resolve, reject) => {
      this.client
        .post(
          `/deposit-instructions/${id}/cancel`,
          payload,
          this.withVersionHeader()
        )
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async listBanks(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniBank>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }
      this.client
        .getList('/banks', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniBank>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async getBank(id: string | number): Promise<AkoniBank> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/banks/${id}`, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async patchBank(
    id: string | number,
    payload: SavingsPatchBankPayload
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .patch(`/banks/${id}`, payload, this.withVersionHeader())
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async listBankDepositInstructions(
    bankId: string | number,
    listData: ListDataParams,
    direction: AkoniInstructionFileDirection = 'deposit'
  ): Promise<ListDataResponse<AkoniDepositInstruction>> {
    return new Promise((resolve, reject) => {
      this.client
        .getList(
          `/banks/${bankId}/deposit-instructions?direction=${direction}`,
          listData,
          this.withVersionHeader()
        )
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniDepositInstruction>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async listProducts(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<AkoniProduct>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }

      this.client
        .getList('/products', listData, this.withVersionHeader())
        .then((res) =>
          resolve(
            normaliseListResponse<AkoniProduct>(
              res,
              listData.page,
              listData.perPage
            )
          )
        )
        .catch((err) => reject(err));
    });
  }

  async getProduct(id: string | number): Promise<AkoniProduct> {
    return new Promise((resolve, reject) => {
      this.client
        .get(`/products/${id}`, this.withVersionHeader())
        .then((res) => resolve(res.data?.data ?? res.data))
        .catch((err) => reject(err));
    });
  }

  async updateProductRate(
    id: string | number,
    fields: AkoniUpdateProductRateFields
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      // PHP only populates $_POST / $_FILES for multipart on POST, not PATCH — use Laravel method spoofing.
      formData.append('_method', 'PATCH');
      formData.append(
        'headline_interest_rate',
        fields.headline_interest_rate.trim()
      );
      formData.append(
        'annual_equivalent_rate',
        fields.annual_equivalent_rate.trim()
      );
      formData.append('effective_from', fields.effective_from);
      formData.append('notice_date', fields.notice_date);
      formData.append('file', fields.file);
      const share = fields.hair_cut_rate?.trim();
      if (share && Number(share) > 0) {
        formData.append('hair_cut_rate', share);
      }

      this.client
        .post(`/products/${id}`, formData, {
          ...this.withVersionHeader(),
          headers: {
            ...this.withVersionHeader().headers,
            // Instance default is application/json; must use multipart so `file` is sent as binary.
            'Content-Type': 'multipart/form-data'
          }
        })
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  async downloadBankInstructionFile(
    bankId: string | number,
    direction: AkoniInstructionFileDirection
  ): Promise<AkoniInstructionFileRun> {
    return new Promise((resolve, reject) => {
      this.client
        .post(
          `/banks/${bankId}/instruction-files`,
          { direction },
          this.withVersionHeader()
        )
        .then((res) => {
          const body = res.data?.data ?? res.data;
          if (body) resolve(body);
          else reject(new Error('Invalid response'));
        })
        .catch((err) => reject(err));
    });
  }

  async deleteBankInstructionFile(
    bankId: string | number,
    runId: string | number
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.client
        .delete(
          `/banks/${bankId}/instruction-files/${runId}`,
          this.withVersionHeader()
        )
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }
}

export { akoniQueryKeys } from './query-keys';
export type { AkoniDepositsListParams, AkoniListParams } from './query-keys';
export {
  useAkoniAccountQuery,
  useAkoniAccountsListQuery,
  useAkoniApplicationQuery,
  useAkoniApplicationsListQuery,
  useAkoniBankDepositInstructionsListQuery,
  useAkoniBankQuery,
  useAkoniBanksListQuery,
  useAkoniDepositInstructionQuery,
  useAkoniDepositInstructionsListQuery,
  useAkoniDepositQuery,
  useAkoniDepositTransactionsQuery,
  useAkoniDepositsListQuery,
  useAkoniDirectBillingReportsListQuery,
  useAkoniIndividualQuery,
  useAkoniIndividualsListQuery,
  useAkoniInstructionQuery,
  useAkoniProductsListQuery,
  useAkoniPublicHolidaysQuery,
  useAkoniTransactionQuery,
  useAkoniTransactionsListQuery
} from './use-akoni-queries';
export {
  useAkoniCancelDepositInstructionMutation,
  useAkoniDeleteBankInstructionFileMutation,
  useAkoniDownloadBankInstructionFileMutation,
  useAkoniExportDepositInstructionsMutation,
  useSavingsPatchBankMutation,
  useAkoniPatchDepositInstructionMutation,
  useAkoniPatchDepositMutation,
  useAkoniUpdateDynamoDbItemMutation
} from './use-akoni-mutations';
export type {
  AkoniCancelDepositInstructionVariables,
  AkoniDeleteBankInstructionFileVariables,
  AkoniDownloadBankInstructionFileVariables,
  AkoniExportDepositInstructionsVariables,
  SavingsPatchBankVariables,
  AkoniPatchDepositInstructionVariables,
  AkoniPatchDepositVariables,
  AkoniUpdateDynamoDbItemVariables
} from './use-akoni-mutations';
